import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Heart, Users, Target, Phone, ArrowRight } from 'lucide-react';
import { companyInfo } from '../utils/mockData';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section
        className="relative bg-cover bg-center py-12"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 71, 171, 0.9), rgba(0, 71, 171, 0.9)), url(https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200)',
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">About Purposefully Covered Insurance Group</h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto">Protecting families with integrity, compassion, and purpose</p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-6">Why I Started Purposefully Covered Insurance Group</h2>
                <p className="text-base text-gray-700 mb-4 leading-relaxed">
                  David Ashley helps families make confident insurance decisions with honest guidance, clear explanations, and coverage options designed around their needs and budget.
                </p>
                <p className="text-base text-gray-700 mb-4 leading-relaxed">
                  Purposefully Covered Insurance Group LLC was founded on the belief that everyone deserves access to quality insurance coverage tailored to their unique needs. Owned and operated by David Ashley, a licensed and insured insurance agent, we help individuals and families secure life insurance, final expense coverage, Medicare options, health and supplemental plans, IULs, annuities, dental, and vision coverage.
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  Our mission is simple: to make sure every client is purposefully covered, never over-insured or under-insured, but protected with the right coverage for their needs. That is what coverage with purpose means to us, and it is the reason behind our name and everything we do.
                </p>
              </div>
              <div>
                <img
                  src="https://customer-assets.emergentagent.com/job_jovial-saha-5/artifacts/qsaqbrqk_IMG_5843.JPG"
                  alt="Our Team"
                  className="rounded-lg shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-12 text-center">Our Core Values</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                { icon: Shield, title: "Integrity", desc: "We operate with honesty and transparency in every interaction. Your trust is our most valuable asset.", color: "bg-brand-blue" },
                { icon: Heart, title: "Compassion", desc: "We understand that insurance decisions can be emotional. We approach every client with empathy and patience.", color: "bg-brand-orange" },
                { icon: Users, title: "Service", desc: "We're committed to providing exceptional service before, during, and after the sale.", color: "bg-brand-blue" },
                { icon: Target, title: "Purpose", desc: "Everything we do is driven by purpose: to protect families, secure legacies, and provide peace of mind.", color: "bg-brand-orange" },
              ].map(({ icon: Icon, title, desc, color }) => (
                <div key={title} className="bg-white p-8 rounded-lg shadow-md">
                  <div className={`${color} text-white w-16 h-16 rounded-full flex items-center justify-center mb-4`}>
                    <Icon size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-brand-blue mb-3">{title}</h3>
                  <p className="text-gray-700 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Meet David Ashley */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-12 text-center">Meet David Ashley</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <p className="text-base text-gray-700 mb-4 leading-relaxed">
                  As a licensed life and health insurance agent, I&apos;ve dedicated my career to helping families secure their financial future. I focus on providing personal guidance, clear explanations, and coverage solutions that actually fit your life.
                </p>
                <p className="text-base text-gray-700 mb-4 leading-relaxed">
                  Whether you&apos;re looking for final expense coverage, Medicare options, life insurance, health and supplemental plans, IULs, annuities, dental, or vision coverage, I&apos;m here to listen, educate, and help you make informed decisions.
                </p>
                <p className="text-base text-gray-700 mb-6 leading-relaxed">
                  I believe in local service, family protection, personal guidance, and no-pressure support. My goal is to help people understand their options so they can make the best choice for their situation.
                </p>
                <a
                  href={`tel:${companyInfo.phoneRaw}`}
                  className="inline-flex items-center gap-2 text-brand-orange font-semibold hover:text-brand-blue transition"
                >
                  <Phone size={18} /> Call {companyInfo.phone}
                </a>
              </div>
              <div className="order-1 lg:order-2">
                <img
                  src="https://customer-assets.emergentagent.com/job_jovial-saha-5/artifacts/jmtbnh10__DSA0690BSC.jpg"
                  alt="David Ashley"
                  className="rounded-lg shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-6">Why Choose Purposefully Covered Insurance Group?</h2>
            <p className="text-base text-gray-700 mb-12 leading-relaxed">
              When you work with Purposefully Covered Insurance Group, you work directly with David Ashley, a licensed and insured insurance agent. We&apos;re your neighbors, your advocates, and your partners in securing financial peace of mind.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
              <div>
                <h3 className="font-bold text-lg text-brand-blue mb-3">Licensed & Experienced</h3>
                <p className="text-gray-700 text-sm">Fully licensed and experienced, with extensive knowledge of insurance products and regulations.</p>
              </div>
              <div>
                <h3 className="font-bold text-lg text-brand-blue mb-3">Client-First Approach</h3>
                <p className="text-gray-700 text-sm">Your needs come first. We listen, educate, and never pressure you into coverage you don&apos;t need.</p>
              </div>
              <div>
                <h3 className="font-bold text-lg text-brand-blue mb-3">Ongoing Support</h3>
                <p className="text-gray-700 text-sm">We&apos;re here for you long after the policy is issued - questions, claims, or changes, we&apos;ve got you covered.</p>
              </div>
            </div>

            <div className="mt-12">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-brand-blue text-white px-8 py-3 rounded-md hover:bg-brand-orange transition font-semibold group"
                data-testid="about-contact-btn"
              >
                Contact Us Today <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
